import React, { useState, useEffect } from "react";
import { useAuth } from "../../hooks/useAuth";
import {
  Appointment,
  AppointmentFormData,
  TimeSlot,
} from "../../services/appointmentService";
import AppointmentService from "../../services/appointmentService";
import PatientDetailsModal from "../patients/PatientDetailsModal";

interface SelectedService {
  service: {
    id: number;
    name: string;
    base_fee: number;
    clinic_percentage: number;
    provider_percentage: number;
    owner_percentage: number;
    referrer_percentage: number;
  };
  referrer_name: string;
  customPrice?: number;
  pricingReason?: string;
}

interface NewPatientData {
  first_name: string;
  last_name: string;
  phone: string;
}

interface AppointmentModalProps {
  isOpen: boolean;
  mode: "create" | "edit" | "view";
  appointment: Appointment | null;
  selectedSlot: { date: string; time: string; doctorId?: number } | null;
  prefilledPatient?: { id: number; name: string; phone: string } | null;
  doctors: any[];
  patients: any[];
  onSave: (
    data: AppointmentFormData,
    newPatientData?: NewPatientData
  ) => Promise<void>;
  onEdit: () => void;
  onDelete?: (appointmentId: number) => Promise<void>;
  onClose: () => void;
  onSaveCustomPricing?: (
    appointmentId: number,
    services: SelectedService[]
  ) => Promise<void>;
  isLoading: boolean;
  // Add this new prop
  onOpenPatientDetails?: (patientId: number) => void;
}

const AppointmentModal: React.FC<AppointmentModalProps> = ({
  isOpen,
  mode,
  appointment,
  selectedSlot,
  prefilledPatient,
  doctors,
  patients,
  onSave,
  onEdit,
  onDelete,
  onClose,
  onSaveCustomPricing,
  isLoading,
  onOpenPatientDetails,
}) => {
  const [formData, setFormData] = useState<AppointmentFormData>({
    patient_id: 0,
    doctor_id: 0,
    appointment_date: "",
    appointment_time: "",
    duration: 30,
    type: "consultation",
    reason: "",
    notes: "",
    patient_notes: "",
    whatsapp_reminder: true,
  });
  const [isPatientDetailsOpen, setIsPatientDetailsOpen] = useState(false);
  const [selectedPatientForDetails, setSelectedPatientForDetails] =
    useState<any>(null);

  // Add this function to handle opening patient details
  const handleOpenPatientDetails = () => {
    if (appointment?.patient) {
      setSelectedPatientForDetails(appointment.patient);
      setIsPatientDetailsOpen(true);
    } else if (formData.patient_id && patients.length > 0) {
      const patient = patients.find((p) => p.id === formData.patient_id);
      if (patient) {
        setSelectedPatientForDetails(patient);
        setIsPatientDetailsOpen(true);
      }
    }
  };

  // Add this function to handle patient details close
  const handlePatientDetailsClose = () => {
    setIsPatientDetailsOpen(false);
    setSelectedPatientForDetails(null);
  };
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Patient search states
  const [searchPatient, setSearchPatient] = useState("");
  const [selectedPatientName, setSelectedPatientName] = useState("");
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);

  // New patient states
  const [activeTab, setActiveTab] = useState<"search" | "new">("search");
  const [newPatientData, setNewPatientData] = useState<NewPatientData>({
    first_name: "",
    last_name: "",
    phone: "",
  });

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { user, canAccessDoctor, isMainSecretary, isAdmin } = useAuth();

  // Responsive states
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  const availableDoctors = doctors.filter((doctor) => {
    if (isAdmin() || isMainSecretary()) {
      return true;
    }

    if (user?.roles?.includes("doctor")) {
      return doctor.id === user.id;
    }

    if (user?.roles?.includes("secretary")) {
      return canAccessDoctor(doctor.id);
    }

    return true;
  });

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Initialize form data
  useEffect(() => {
    if (mode === "create" && selectedSlot) {
      setFormData((prev) => ({
        ...prev,
        appointment_date: selectedSlot.date,
        appointment_time: selectedSlot.time,
        doctor_id:
          selectedSlot.doctorId ||
          (availableDoctors.length > 0 ? availableDoctors[0].id : 0),
      }));
    } else if (mode === "create" && prefilledPatient && !selectedSlot) {
      setFormData((prev) => {
        const newData = {
          ...prev,
          patient_id: prefilledPatient.id,
          doctor_id: availableDoctors.length > 0 ? availableDoctors[0].id : 0,
          appointment_date: "",
          appointment_time: "",
        };
        return newData;
      });
      setSelectedPatientName(prefilledPatient.name);
      setSearchPatient(prefilledPatient.name);
      setActiveTab("search"); // Keep search tab active for prefilled patients
    } else if ((mode === "edit" || mode === "view") && appointment) {
      setFormData({
        patient_id: appointment.patient_id,
        doctor_id: appointment.doctor_id,
        appointment_date: appointment.appointment_date,
        appointment_time: appointment.appointment_time,
        duration: appointment.duration,
        type: appointment.type,
        reason: appointment.reason || "",
        notes: appointment.notes || "",
        patient_notes: appointment.patient_notes || "",
        whatsapp_reminder: appointment.whatsapp_reminder,
      });

      setSelectedPatientName(appointment.patient.full_name);
      setSearchPatient(appointment.patient.full_name);
      setActiveTab("search");
    }
  }, [mode, appointment, selectedSlot, prefilledPatient]);

  useEffect(() => {
    if (
      mode === "create" &&
      availableDoctors.length > 0 &&
      formData.doctor_id === 0
    ) {
      setFormData((prev) => ({
        ...prev,
        doctor_id: availableDoctors[0].id,
      }));
    }
  }, [availableDoctors, mode, formData.doctor_id]);

  useEffect(() => {
    if (mode !== "view" && availableDoctors.length > 0) {
      if (
        formData.doctor_id === 0 ||
        !availableDoctors.find((doc) => doc.id === formData.doctor_id)
      ) {
        setFormData((prev) => ({
          ...prev,
          doctor_id: availableDoctors[0].id,
        }));
      }

      if (errors.doctor_id) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.doctor_id;
          return newErrors;
        });
      }
    } else if (mode !== "view" && availableDoctors.length === 0) {
      setErrors((prev) => ({
        ...prev,
        doctor_id: "No doctors available. Please add a doctor first.",
      }));
    }
  }, [availableDoctors, mode, formData.doctor_id, errors.doctor_id]);

  // Load available slots when doctor or date changes
  useEffect(() => {
    if (mode !== "view" && formData.doctor_id && formData.appointment_date) {
      loadAvailableSlots();
    }
  }, [formData.doctor_id, formData.appointment_date, mode]);

  const loadAvailableSlots = async () => {
    try {
      setLoadingSlots(true);

      const selectedDate = new Date(formData.appointment_date + "T00:00:00");
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        const pastDateSlots: TimeSlot[] = [];

        for (let hour = 8; hour <= 18; hour++) {
          for (let minute = 0; minute < 60; minute += 15) {
            if (hour === 18 && minute > 0) break;

            const timeString = `${hour.toString().padStart(2, "0")}:${minute
              .toString()
              .padStart(2, "0")}`;
            const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
            const ampm = hour >= 12 ? "PM" : "AM";
            const formattedTime = `${displayHour}:${minute
              .toString()
              .padStart(2, "0")} ${ampm}`;

            pastDateSlots.push({
              time: timeString,
              formatted_time: formattedTime,
              available: true,
            });
          }
        }

        setAvailableSlots(pastDateSlots);
      } else {
        const slots = await AppointmentService.getAvailableSlots(
          formData.doctor_id,
          formData.appointment_date
        );
        setAvailableSlots(slots);
      }
    } catch (error) {
      console.error("Failed to load available slots:", error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name === "doctor_id"
          ? parseInt(value)
          : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleNewPatientChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewPatientData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear patient_id when switching to new patient
    if (formData.patient_id !== 0) {
      setFormData((prev) => ({ ...prev, patient_id: 0 }));
    }

    // Clear errors for patient fields
    if (errors.patient_id) {
      setErrors((prev) => ({ ...prev, patient_id: "" }));
    }
  };

  const handlePatientSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setSearchPatient(value);

    if (value.length >= 2) {
      setShowPatientDropdown(true);
    } else {
      setShowPatientDropdown(false);
      if (formData.patient_id !== 0) {
        setFormData((prev) => ({ ...prev, patient_id: 0 }));
        setSelectedPatientName("");
      }
    }

    if (errors.patient_id) {
      setErrors((prev) => ({ ...prev, patient_id: "" }));
    }
  };

  const handlePatientSelect = (patient: any) => {
    setFormData((prev) => ({ ...prev, patient_id: patient.id }));
    setSelectedPatientName(patient.first_name + " " + patient.last_name);
    setSearchPatient(patient.first_name + " " + patient.last_name);
    setShowPatientDropdown(false);

    // Clear new patient data when selecting existing patient
    setNewPatientData({
      first_name: "",
      last_name: "",
      phone: "",
    });
  };

  const handleTabSwitch = (tab: "search" | "new") => {
    setActiveTab(tab);

    if (tab === "new") {
      // Clear existing patient selection
      setFormData((prev) => ({ ...prev, patient_id: 0 }));
      setSelectedPatientName("");
      setSearchPatient("");
      setShowPatientDropdown(false);
    } else {
      // Clear new patient data
      setNewPatientData({
        first_name: "",
        last_name: "",
        phone: "",
      });
    }

    // Clear patient errors
    if (errors.patient_id) {
      setErrors((prev) => ({ ...prev, patient_id: "" }));
    }
  };

  const filteredPatients =
    searchPatient.length >= 2
      ? patients.filter(
          (patient) =>
            `${patient.first_name} ${patient.last_name}`
              .toLowerCase()
              .includes(searchPatient.toLowerCase()) ||
            patient.phone.includes(searchPatient) ||
            (patient.mother_name &&
              patient.mother_name
                .toLowerCase()
                .includes(searchPatient.toLowerCase()))
        )
      : [];

  const formatPatientName = (patient: any): string => {
    let display = `${patient.first_name} ${patient.last_name} - ${patient.phone}`;
    if (patient.mother_name) {
      display += ` (Mother: ${patient.mother_name})`;
    }
    return display;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Patient validation based on active tab
    if (activeTab === "search") {
      if (!formData.patient_id) {
        newErrors.patient_id = "Please select a patient";
      }
    } else {
      // New patient validation
      if (!newPatientData.first_name.trim()) {
        newErrors.first_name = "First name is required";
      }
      if (!newPatientData.last_name.trim()) {
        newErrors.last_name = "Last name is required";
      }
      if (!newPatientData.phone.trim()) {
        newErrors.phone = "Phone number is required";
      }
    }

    if (!formData.doctor_id) newErrors.doctor_id = "Please select a doctor";
    if (!formData.appointment_date)
      newErrors.appointment_date = "Please select a date";
    if (!formData.appointment_time)
      newErrors.appointment_time = "Please select a time";
    if (!formData.type) newErrors.type = "Please select appointment type";

    if (formData.doctor_id && doctors.length > 0) {
      const selectedDoctor = doctors.find(
        (doc) => doc.id === formData.doctor_id
      );
      if (!selectedDoctor) {
        newErrors.doctor_id =
          "Selected doctor not found. Please select another doctor.";
      }
    }

    if (
      mode === "create" &&
      formData.appointment_time &&
      availableSlots.length > 0
    ) {
      const selectedSlot = availableSlots.find(
        (slot) => slot.time === formData.appointment_time
      );
      if (!selectedSlot?.available) {
        newErrors.appointment_time = "Selected time slot is not available";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (
        activeTab === "new" &&
        newPatientData.first_name &&
        newPatientData.last_name
      ) {
        // Pass new patient data to onSave
        await onSave(formData, newPatientData);
      } else {
        // Regular save with existing patient
        await onSave(formData);
      }
    } catch (error: any) {
      setErrors({ submit: error.message || "Failed to save appointment" });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  const handleDelete = async () => {
    if (onDelete && appointment) {
      try {
        await onDelete(appointment.id);
        onClose();
      } catch (error: any) {
        setErrors({ submit: error.message || "Failed to delete appointment" });
      }
    }
  };

  const handlePatientInputBlur = () => {
    setTimeout(() => {
      setShowPatientDropdown(false);
    }, 200);
  };

  const appointmentTypes = AppointmentService.getAppointmentTypes();

  if (!isOpen) return null;

  // Responsive Styles
  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: isMobile ? "flex-end" : "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: isMobile ? "0" : "20px",
    overflowX: "hidden",
    maxWidth: "100vw",
  };

  const modalStyle: React.CSSProperties = {
    background: "#ffffff",
    borderRadius: isMobile ? "16px 16px 0 0" : "12px",
    maxWidth: isMobile ? "100%" : isTablet ? "90%" : "600px",
    width: isMobile ? "100%" : "100%",
    maxHeight: isMobile ? "95vh" : "90vh",
    height: isMobile ? "auto" : "auto",
    overflow: "hidden",
    boxShadow: isMobile
      ? "0 -10px 25px rgba(0, 0, 0, 0.25)"
      : "0 25px 50px rgba(0, 0, 0, 0.25)",
    display: "flex",
    flexDirection: "column",
    transform: isMobile ? "translateY(0)" : "none",
    animation: isMobile ? "slideUp 0.3s ease-out" : "none",
    margin: isMobile ? "0" : "auto",
    position: "relative",
  };

  const headerStyle: React.CSSProperties = {
    paddingTop: isMobile ? "20px" : "24px",
    paddingLeft: isMobile ? "20px" : "24px",
    paddingRight: isMobile ? "20px" : "24px",
    paddingBottom: "16px",
    borderBottom: "1px solid #e5e7eb",
    flexShrink: 0,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: isMobile ? "18px" : "20px",
    fontWeight: "600",
    color: "#1f2937",
    margin: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  const contentStyle: React.CSSProperties = {
    padding: isMobile ? "20px" : "24px",
    overflow: "auto",
    flex: 1,
  };

  const formGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: isMobile
      ? "1fr"
      : "repeat(auto-fit, minmax(200px, 1fr))",
    gap: isMobile ? "20px" : "24px",
    marginBottom: "20px",
    width: "100%",
    maxWidth: "100%",
  };

  const fieldStyle: React.CSSProperties = {
    marginBottom: isMobile ? "20px" : "16px",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "14px",
    fontWeight: "500",
    color: "#374151",
    marginBottom: "6px",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: isMobile ? "12px 14px" : "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "14px",
    outline: "none",
    transition: "border-color 0.2s ease",
    minHeight: isMobile ? "44px" : "auto",
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    cursor: "pointer",
  };

  const textareaStyle: React.CSSProperties = {
    ...inputStyle,
    resize: "vertical" as const,
    minHeight: isMobile ? "80px" : "80px",
  };

  const errorStyle: React.CSSProperties = {
    color: "#ef4444",
    fontSize: "12px",
    marginTop: "4px",
  };

  const buttonStyle = (
    variant: "primary" | "secondary" | "danger" = "secondary"
  ): React.CSSProperties => {
    const variants = {
      primary: { bg: "#3b82f6", text: "#ffffff" },
      secondary: { bg: "#f3f4f6", text: "#374151" },
      danger: { bg: "#ef4444", text: "#ffffff" },
    };

    const colors = variants[variant];

    return {
      padding: isMobile ? "12px 16px" : "10px 16px",
      fontSize: "14px",
      fontWeight: "500",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      background: colors.bg,
      color: colors.text,
      minHeight: isMobile ? "44px" : "auto",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    };
  };

  const tabStyle = (isActive: boolean): React.CSSProperties => ({
    padding: "10px 16px",
    backgroundColor: isActive ? "#3b82f6" : "#f3f4f6",
    color: isActive ? "#ffffff" : "#6b7280",
    border: "none",
    borderRadius: "6px 6px 0 0",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    flex: 1,
    transition: "all 0.2s ease",
  });

  const footerStyle: React.CSSProperties = {
    padding: isMobile ? "16px 20px 20px 20px" : "16px 24px",
    borderTop: "1px solid #e5e7eb",
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    justifyContent: "space-between",
    alignItems: isMobile ? "stretch" : "center",
    gap: isMobile ? "12px" : "8px",
    flexShrink: 0,
    background: "#f9fafb",
  };

  const mobileButtonRowStyle: React.CSSProperties = {
    display: "flex",
    gap: "8px",
    order: isMobile ? 2 : 1,
  };

  const timeSlotsGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: isMobile
      ? "repeat(auto-fill, minmax(60px, 1fr))"
      : "repeat(auto-fill, minmax(80px, 1fr))",
    gap: isMobile ? "6px" : "8px",
    marginTop: "8px",
    width: "100%",
    maxWidth: "100%",
  };

  const isPastAppointment = (date: string, time: string): boolean => {
    if (!date || !time) return false;
    const appointmentDateTime = new Date(`${date}T${time}:00`);
    return appointmentDateTime < new Date();
  };

  const timeSlotStyle = (slot: TimeSlot): React.CSSProperties => {
    const isPast =
      formData.appointment_date &&
      isPastAppointment(formData.appointment_date, slot.time);

    const isSelected = slot.time === formData.appointment_time;

    return {
      padding: isMobile ? "10px 8px" : "8px 12px",
      fontSize: isMobile ? "11px" : "12px",
      border: `1px solid ${
        isPast ? "#fbbf24" : slot.available ? "#d1d5db" : "#fca5a5"
      }`,
      borderRadius: "6px",
      cursor: slot.available ? "pointer" : "not-allowed",
      textAlign: "center",
      background: isSelected
        ? "#3b82f6"
        : isPast
        ? "#fef3c7"
        : slot.available
        ? "#ffffff"
        : "#fef2f2",
      color: isSelected
        ? "#ffffff"
        : isPast
        ? "#92400e"
        : slot.available
        ? "#374151"
        : "#9ca3af",
      transition: "all 0.2s ease",
      minHeight: isMobile ? "44px" : "auto",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
    };
  };

  const dropdownStyle: React.CSSProperties = {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    background: "#ffffff",
    border: "1px solid #d1d5db",
    borderTop: "none",
    borderRadius: "0 0 6px 6px",
    maxHeight: isMobile ? "150px" : "200px",
    overflowY: "auto",
    zIndex: 1000,
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  };

  const dropdownItemStyle: React.CSSProperties = {
    padding: isMobile ? "12px" : "10px 12px",
    cursor: "pointer",
    borderBottom: "1px solid #f3f4f6",
    fontSize: "14px",
    transition: "background-color 0.2s",
  };

  const getModalTitle = () => {
    switch (mode) {
      case "create":
        return "New Appointment";
      case "edit":
        return "Edit Appointment";
      case "view":
        return "Appointment Details";
      default:
        return "Appointment";
    }
  };

  return (
    <>
      <div
        style={overlayStyle}
        onClick={onClose}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div style={headerStyle}>
            <h2 style={titleStyle} className="nav-controls">
              {getModalTitle()}
              <button
                onClick={onClose}
                style={{
                  ...buttonStyle("secondary"),
                  padding: isMobile ? "8px" : "6px 8px",
                  fontSize: "16px",
                  minHeight: "auto",
                }}
              >
                ✕
              </button>
            </h2>
          </div>

          {/* Content */}
          <div style={contentStyle}>
            {mode === "view" ? (
              // View Mode - Enhanced patient section
              <div>
                <div style={formGridStyle}>
                  <div>
                    <strong style={labelStyle}>Patient</strong>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#374151",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: "8px",
                      }}
                    >
                      <div>
                        {appointment?.patient.full_name}
                        {appointment?.patient.mother_name && (
                          <div style={{ fontSize: "12px", color: "#6b7280" }}>
                            Mother: {appointment.patient.mother_name}
                          </div>
                        )}
                      </div>
                      {/* Patient Details Button */}
                      <button
                        type="button"
                        onClick={handleOpenPatientDetails}
                        style={{
                          ...buttonStyle("secondary"),
                          padding: isMobile ? "6px 12px" : "4px 8px",
                          fontSize: "12px",
                          minHeight: "auto",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          border: "1px solid #d1d5db",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f3f4f6";
                          e.currentTarget.style.borderColor = "#9ca3af";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#ffffff";
                          e.currentTarget.style.borderColor = "#d1d5db";
                        }}
                      >
                        <span>👤</span>
                        <span>Details</span>
                      </button>
                    </div>
                  </div>

                  {/* Rest of the existing view mode content... */}
                  <div>
                    <strong style={labelStyle}>Doctor</strong>
                    <div style={{ fontSize: "14px", color: "#374151" }}>
                      Dr. {appointment?.doctor.full_name}
                      {appointment?.doctor.specialty && (
                        <div style={{ fontSize: "12px", color: "#6b7280" }}>
                          {appointment.doctor.specialty}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* Continue with rest of view mode content... */}
              </div>
            ) : (
              // Create/Edit Mode
              <form onSubmit={handleSubmit}>
                <div style={formGridStyle}>
                  {/* Patient Selection with Tabs */}
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Patient *</label>

                    {/* Tab Navigation */}
                    <div
                      style={{ display: "flex", marginBottom: "12px" }}
                      className="nav-controls"
                    >
                      <button
                        type="button"
                        onClick={() => handleTabSwitch("search")}
                        style={tabStyle(activeTab === "search")}
                      >
                        Search Patient
                      </button>
                      <button
                        type="button"
                        onClick={() => handleTabSwitch("new")}
                        style={{
                          ...tabStyle(activeTab === "new"),
                          borderRadius: "0 6px 0 0",
                        }}
                      >
                        Add New Patient
                      </button>
                    </div>

                    {/* Tab Content */}
                    {activeTab === "search" ? (
                      /* Search Patient Tab */
                      <div style={{ position: "relative" }}>
                        <input
                          type="text"
                          id="search_patient"
                          name="search_patient"
                          placeholder="Search patients by name or phone..."
                          value={searchPatient}
                          onChange={handlePatientSearchChange}
                          onBlur={handlePatientInputBlur}
                          onFocus={() => {
                            if (searchPatient.length >= 2) {
                              setShowPatientDropdown(true);
                            }
                          }}
                          style={{
                            ...inputStyle,
                            borderColor: formData.patient_id
                              ? "#10b981"
                              : "#d1d5db",
                          }}
                        />

                        {/* Patient Dropdown */}
                        {showPatientDropdown && filteredPatients.length > 0 && (
                          <div style={dropdownStyle}>
                            {filteredPatients.map((patient) => (
                              <div
                                key={patient.id}
                                onClick={() => handlePatientSelect(patient)}
                                style={dropdownItemStyle}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#f9fafb";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#ffffff";
                                }}
                              >
                                {formatPatientName(patient)}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Show selected patient info */}
                        {formData.patient_id > 0 && selectedPatientName && (
                          <div
                            style={{
                              marginTop: "8px",
                              padding: "8px 12px",
                              background: "#f0fdf4",
                              border: "1px solid #bbf7d0",
                              borderRadius: "6px",
                              fontSize: "13px",
                              color: "#166534",
                            }}
                          >
                            ✓ Selected: {selectedPatientName}
                          </div>
                        )}

                        {/* Show message when user needs to type more */}
                        {searchPatient.length > 0 &&
                          searchPatient.length < 2 && (
                            <div
                              style={{
                                marginTop: "8px",
                                fontSize: "12px",
                                color: "#6b7280",
                                fontStyle: "italic",
                              }}
                            >
                              Type at least 2 characters to search patients...
                            </div>
                          )}

                        {/* Show no results message */}
                        {searchPatient.length >= 2 &&
                          filteredPatients.length === 0 && (
                            <div
                              style={{
                                marginTop: "8px",
                                padding: "8px 12px",
                                background: "#fef2f2",
                                border: "1px solid #fecaca",
                                borderRadius: "6px",
                                fontSize: "13px",
                                color: "#dc2626",
                              }}
                            >
                              No patients found matching "{searchPatient}"
                            </div>
                          )}
                      </div>
                    ) : (
                      /* Add New Patient Tab */
                      <div
                        style={{
                          background: "#f8fafc",
                          padding: "16px",
                          borderRadius: "0 6px 6px 6px",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <div style={{ marginBottom: "12px" }}>
                          <label style={labelStyle}>First Name *</label>
                          <input
                            type="text"
                            name="first_name"
                            placeholder="Enter first name"
                            value={newPatientData.first_name}
                            onChange={handleNewPatientChange}
                            style={{
                              ...inputStyle,
                              borderColor: errors.first_name
                                ? "#ef4444"
                                : "#d1d5db",
                            }}
                          />
                          {errors.first_name && (
                            <div style={errorStyle}>{errors.first_name}</div>
                          )}
                        </div>

                        <div style={{ marginBottom: "12px" }}>
                          <label style={labelStyle}>Last Name *</label>
                          <input
                            type="text"
                            name="last_name"
                            placeholder="Enter last name"
                            value={newPatientData.last_name}
                            onChange={handleNewPatientChange}
                            style={{
                              ...inputStyle,
                              borderColor: errors.last_name
                                ? "#ef4444"
                                : "#d1d5db",
                            }}
                          />
                          {errors.last_name && (
                            <div style={errorStyle}>{errors.last_name}</div>
                          )}
                        </div>

                        <div style={{ marginBottom: "8px" }}>
                          <label style={labelStyle}>Phone Number *</label>
                          <input
                            type="tel"
                            name="phone"
                            placeholder="Enter phone number"
                            value={newPatientData.phone}
                            onChange={handleNewPatientChange}
                            style={{
                              ...inputStyle,
                              borderColor: errors.phone ? "#ef4444" : "#d1d5db",
                            }}
                          />
                          {errors.phone && (
                            <div style={errorStyle}>{errors.phone}</div>
                          )}
                        </div>

                        {/* Show preview of new patient */}
                        {(newPatientData.first_name ||
                          newPatientData.last_name) && (
                          <div
                            style={{
                              marginTop: "8px",
                              padding: "8px 12px",
                              background: "#eff6ff",
                              border: "1px solid #bfdbfe",
                              borderRadius: "6px",
                              fontSize: "13px",
                              color: "#1e40af",
                            }}
                          >
                            New patient: {newPatientData.first_name}{" "}
                            {newPatientData.last_name}
                            {newPatientData.phone &&
                              ` - ${newPatientData.phone}`}
                          </div>
                        )}
                      </div>
                    )}

                    {errors.patient_id && (
                      <div style={errorStyle}>{errors.patient_id}</div>
                    )}
                  </div>

                  {/* Doctor Selection */}
                  <div style={fieldStyle}>
                    <label htmlFor="doctor_id" style={labelStyle}>
                      Doctor *
                      {!isAdmin() && !isMainSecretary() && (
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            fontWeight: "normal",
                          }}
                        >
                          {user?.roles?.includes("doctor")
                            ? " (You can only book for yourself)"
                            : user?.assigned_doctor_id
                            ? " (Limited to assigned doctor)"
                            : ""}
                        </span>
                      )}
                    </label>
                    <select
                      id="doctor_id"
                      name="doctor_id"
                      value={formData.doctor_id}
                      onChange={handleInputChange}
                      style={{
                        ...selectStyle,
                        borderColor: errors.doctor_id ? "#ef4444" : "#d1d5db",
                      }}
                      required
                      disabled={availableDoctors.length === 1}
                    >
                      <option value="">Select a doctor</option>
                      {availableDoctors.map((doctor: any) => (
                        <option key={doctor.id} value={doctor.id}>
                          Dr. {doctor.first_name} {doctor.last_name}
                          {doctor.specialty && ` - ${doctor.specialty}`}
                        </option>
                      ))}
                    </select>
                    {availableDoctors.length === 0 && (
                      <div
                        style={{
                          ...errorStyle,
                          background: "#fef2f2",
                          padding: "8px 12px",
                          borderRadius: "4px",
                          border: "1px solid #fecaca",
                          marginTop: "4px",
                        }}
                      >
                        No doctors available for booking. Please contact an
                        administrator.
                      </div>
                    )}
                    {availableDoctors.length === 1 && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#059669",
                          marginTop: "4px",
                          fontStyle: "italic",
                        }}
                      >
                        Automatically selected based on your permissions
                      </div>
                    )}
                    {errors.doctor_id && (
                      <div style={errorStyle}>{errors.doctor_id}</div>
                    )}
                  </div>
                </div>

                <div style={formGridStyle}>
                  {/* Date */}
                  <div style={fieldStyle}>
                    <label htmlFor="appointment_date" style={labelStyle}>
                      Date *
                    </label>
                    <input
                      id="appointment_date"
                      type="date"
                      name="appointment_date"
                      value={formData.appointment_date}
                      onChange={handleInputChange}
                      style={inputStyle}
                      required
                    />
                    {formData.appointment_date &&
                      formData.appointment_time &&
                      isPastAppointment(
                        formData.appointment_date,
                        formData.appointment_time
                      ) && (
                        <div
                          style={{
                            marginTop: "8px",
                            padding: "8px 12px",
                            background: "#fef3c7",
                            border: "1px solid #fbbf24",
                            borderRadius: "6px",
                            fontSize: "13px",
                            color: "#92400e",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <span>⚠️</span>
                          <span>This appointment time is in the past</span>
                        </div>
                      )}

                    {errors.appointment_date && (
                      <div style={errorStyle}>{errors.appointment_date}</div>
                    )}
                  </div>

                  {/* Duration */}
                  <div style={fieldStyle}>
                    <label htmlFor="duration" style={labelStyle}>
                      Duration (minutes)
                    </label>
                    <select
                      id="duration"
                      name="duration"
                      value={formData.duration}
                      onChange={handleInputChange}
                      style={selectStyle}
                    >
                      <option value="15">15 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="45">45 minutes</option>
                      <option value="60">1 hour</option>
                      <option value="90">1.5 hours</option>
                      <option value="120">2 hours</option>
                    </select>
                  </div>
                </div>

                {/* Available Time Slots */}
                {formData.doctor_id && formData.appointment_date && (
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Available Time Slots *</label>

                    {/* Past Date Warning for Time Slots */}
                    {formData.appointment_date &&
                      new Date(formData.appointment_date + "T23:59:59") <
                        new Date() && (
                        <div
                          style={{
                            marginBottom: "12px",
                            padding: "10px 12px",
                            background: "#fef3c7",
                            border: "1px solid #fbbf24",
                            borderRadius: "6px",
                            fontSize: "13px",
                            color: "#92400e",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <span style={{ fontSize: "16px" }}>⚠️</span>
                          <div>
                            <div
                              style={{ fontWeight: "500", marginBottom: "2px" }}
                            >
                              Selected date is in the past
                            </div>
                            <div style={{ fontSize: "12px", opacity: 0.9 }}>
                              You can still book this appointment for
                              record-keeping purposes
                            </div>
                          </div>
                        </div>
                      )}

                    {loadingSlots ? (
                      <div
                        style={{
                          padding: "20px",
                          textAlign: "center",
                          color: "#6b7280",
                        }}
                      >
                        Loading available slots...
                      </div>
                    ) : availableSlots.length > 0 ? (
                      <div style={timeSlotsGridStyle} className="keep-grid">
                        {availableSlots.map((slot) => (
                          <button
                            key={slot.time}
                            type="button"
                            onClick={() => {
                              if (slot.available) {
                                setFormData((prev) => ({
                                  ...prev,
                                  appointment_time: slot.time,
                                }));
                              }
                            }}
                            style={timeSlotStyle(slot)}
                            disabled={!slot.available}
                            title={
                              isPastAppointment(
                                formData.appointment_date,
                                slot.time
                              )
                                ? "This time is in the past"
                                : slot.available
                                ? "Available"
                                : "Not available"
                            }
                          >
                            {slot.formatted_time}
                            {/* Show past indicator on individual time slots */}
                            {isPastAppointment(
                              formData.appointment_date,
                              slot.time
                            ) && (
                              <div
                                style={{
                                  fontSize: "10px",
                                  opacity: 0.8,
                                  marginTop: "2px",
                                  textAlign: "center",
                                }}
                              >
                                (past)
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div
                        style={{
                          padding: "20px",
                          textAlign: "center",
                          color: "#6b7280",
                        }}
                      >
                        No available slots for this date
                      </div>
                    )}
                    {errors.appointment_time && (
                      <div style={errorStyle}>{errors.appointment_time}</div>
                    )}
                  </div>
                )}

                <div style={formGridStyle}>
                  {/* Appointment Type */}
                  <div style={fieldStyle}>
                    <label htmlFor="type" style={labelStyle}>
                      Type *
                    </label>
                    <select
                      id="type"
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      style={selectStyle}
                      required
                    >
                      {appointmentTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    {errors.type && <div style={errorStyle}>{errors.type}</div>}
                  </div>

                  {/* WhatsApp Reminder */}
                  <div style={fieldStyle}>
                    <label
                      style={{
                        ...labelStyle,
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        minHeight: isMobile ? "44px" : "auto",
                      }}
                    >
                      <input
                        type="checkbox"
                        id="whatsapp_reminder"
                        name="whatsapp_reminder"
                        checked={formData.whatsapp_reminder}
                        onChange={handleInputChange}
                        style={{
                          marginRight: "8px",
                          minWidth: isMobile ? "20px" : "16px",
                          minHeight: isMobile ? "20px" : "16px",
                        }}
                      />
                      WhatsApp Reminder
                    </label>
                  </div>
                </div>

                {/* Reason */}
                <div style={fieldStyle}>
                  <label htmlFor="reason" style={labelStyle}>
                    Reason for Visit
                  </label>
                  <textarea
                    id="reason"
                    name="reason"
                    value={formData.reason}
                    onChange={handleInputChange}
                    placeholder="Brief description of the reason for this appointment..."
                    style={textareaStyle}
                  />
                </div>

                {/* Patient Notes */}
                <div style={fieldStyle}>
                  <label htmlFor="patient_notes" style={labelStyle}>
                    Patient Notes
                  </label>
                  <textarea
                    id="patient_notes"
                    name="patient_notes"
                    value={formData.patient_notes}
                    onChange={handleInputChange}
                    placeholder="Any additional information from the patient..."
                    style={textareaStyle}
                  />
                </div>

                {/* Internal Notes */}
                <div style={fieldStyle}>
                  <label htmlFor="notes" style={labelStyle}>
                    Internal Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Internal notes for staff..."
                    style={textareaStyle}
                  />
                </div>

                {errors.submit && (
                  <div
                    style={{
                      ...errorStyle,
                      fontSize: "14px",
                      padding: "12px",
                      background: "#fef2f2",
                      borderRadius: "6px",
                      border: "1px solid #fca5a5",
                      marginBottom: "16px",
                    }}
                  >
                    {errors.submit}
                  </div>
                )}
              </form>
            )}
          </div>

          {/* Footer */}
          <div style={footerStyle}>
            <div style={mobileButtonRowStyle}>
              {mode === "view" && appointment?.can_be_rescheduled && (
                <button
                  onClick={onEdit}
                  style={{
                    ...buttonStyle("secondary"),
                    flex: isMobile ? "1" : "initial",
                  }}
                >
                  ✏️ Edit
                </button>
              )}

              {/* Delete Button */}
              {mode === "view" && onDelete && appointment && (
                <>
                  {showDeleteConfirm ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        gap: "8px",
                        alignItems: "center",
                        flex: isMobile ? "1" : "initial",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "14px",
                          color: "#dc2626",
                          marginBottom: isMobile ? "8px" : "0",
                        }}
                      >
                        Delete permanently?
                      </span>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          width: isMobile ? "100%" : "auto",
                        }}
                      >
                        <button
                          onClick={handleDelete}
                          style={{
                            ...buttonStyle("danger"),
                            flex: isMobile ? "1" : "initial",
                          }}
                        >
                          🗑️ Delete
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(false)}
                          style={{
                            ...buttonStyle("secondary"),
                            flex: isMobile ? "1" : "initial",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      style={{
                        ...buttonStyle("danger"),
                        flex: isMobile ? "1" : "initial",
                      }}
                    >
                      🗑️ Delete
                    </button>
                  )}
                </>
              )}
            </div>

            <div
              style={{
                display: "flex",
                gap: "8px",
                order: isMobile ? 1 : 2,
              }}
              className="nav-controls"
            >
              <button
                onClick={onClose}
                style={{
                  ...buttonStyle("secondary"),
                  flex: isMobile ? "1" : "initial",
                }}
                type="button"
              >
                {mode === "view" ? "Close" : "Cancel"}
              </button>

              {mode !== "view" && (
                <button
                  onClick={handleSubmit}
                  style={{
                    ...buttonStyle("primary"),
                    opacity: isLoading ? 0.7 : 1,
                    cursor: isLoading ? "not-allowed" : "pointer",
                    flex: isMobile ? "1" : "initial",
                  }}
                  disabled={isLoading}
                  type="submit"
                >
                  {isLoading
                    ? "Saving..."
                    : mode === "create"
                    ? "Book Appointment"
                    : "Update Appointment"}
                </button>
              )}
            </div>
          </div>
        </div>
        {isPatientDetailsOpen && selectedPatientForDetails && (
          <PatientDetailsModal
            patient={selectedPatientForDetails}
            isOpen={isPatientDetailsOpen}
            onClose={handlePatientDetailsClose}
            onPatientUpdated={(updatedPatient) => {
              // Optionally update the patient in the local state
              // You might want to call a parent component method to refresh patient data
              handlePatientDetailsClose();
            }}
          />
        )}
      </div>

      {/* Global Styles */}
      <style>
        {`
          @keyframes slideUp {
            from {
              transform: translateY(100%);
            }
            to {
              transform: translateY(0);
            }
          }
          
          /* Mobile scrollbar styling */
          @media (max-width: 768px) {
            .patient-dropdown::-webkit-scrollbar {
              width: 4px;
            }
            
            .patient-dropdown::-webkit-scrollbar-track {
              background: #f1f1f1;
            }
            
            .patient-dropdown::-webkit-scrollbar-thumb {
              background: #c1c1c1;
              border-radius: 2px;
            }
          }
          
          /* Focus styles for accessibility */
          input:focus, select:focus, textarea:focus, button:focus {
            outline: 2px solid #3b82f6;
            outline-offset: 2px;
          }
          
          /* Touch targets */
          @media (max-width: 768px) {
            button, input, select, textarea {
              min-height: 44px;
            }
            
            input[type="checkbox"] {
              min-width: 20px;
              min-height: 20px;
            }
          }
        `}
      </style>
    </>
  );
};

export default AppointmentModal;
